import type { ControlIn, IControl } from "@/controls/control";
import { Control, extendIn, mergeIn } from "@/controls/control";
import { watchEffect } from "vue";
import type { CreateElement, ShallowReactive } from "vue";
import BmBtn from "@/components/controls/input/BmBtn.vue";
import type { Location } from "vue-router";
import type { PartialQ } from "@/controls/tpcontrol";
import {
  controlDefaults,
  extendDefaults,
  TpControl,
} from "@/controls/tpcontrol";
import { tpLayouts } from "@/tradeportal/tpcontrols/tpLayouts";
import { VBottomSheet, VSheet } from "vuetify/lib";
import { makeRef } from "@/components/composables/reactivityTypes";
import messages from "@/plugins/i18n";
import { screenSize } from "@/vue23switching/screenSize";

type ButtonType = "primary" | "secondary" | "ghost";
type ButtonIn = ControlIn & {
  type: ButtonType;
  text: string;
  toLocation: Location | null;
  fullWidth: boolean;
};

export class Button extends Control {
  #in: ShallowReactive<ButtonIn>;
  public onClick: () => void = () => {};

  get in() {
    return this.#in;
  }

  constructor(type: ButtonType, initialText: string) {
    super();
    this.#in = extendIn(super.in, {
      type,
      text: initialText,
      toLocation: null,
      fullWidth: false,
    });
  }

  render(h: any) {
    const inp = () => this.onClick();

    return h(BmBtn, {
      props: {
        buttonType: this.in.type,
        label: this.in.text,
      },
      attrs: {
        disabled: !this.resolveEnabled(),
        to: this.in.toLocation,
        block: this.in.fullWidth,
      },
      on: {
        click: inp,
      },
    });
  }
}

const moreButtonDefaults = extendDefaults(controlDefaults, {
  text: "", // Will be resolved to translation for "More" when button constructed
  fullWidth: false,
  buttons: [] as readonly IControl[],
  moreMode: "sheet" as const, // Sheet is a bottom sheet. May add Eg popup menu in future.
});

export type MoreButtonIn = typeof moreButtonDefaults;

export class MoreButton extends TpControl<MoreButtonIn> {
  private _showBottomSheet = makeRef(false);

  render(h: CreateElement) {
    const inp = () => (this._showBottomSheet.value = true);
    return h(
      VBottomSheet,
      {
        props: {
          value: this._showBottomSheet.value,
        },
        on: {
          input: (val: boolean) => {
            this._showBottomSheet.value = val;
          },
        },
        scopedSlots: {
          activator: () => {
            return h(BmBtn, {
              props: {
                buttonType: "secondary",
                label: this.in.text,
              },
              attrs: {
                disabled: !this.resolveEnabled(),
                block: this.in.fullWidth,
              },
              on: {
                click: inp,
              },
            });
          },
        },
      },
      [
        h(VSheet, { class: "button-bar" }, [
          h(
            "div",
            {
              class: "sheet-container flex",
            },
            this.in.buttons.filter((x) => x.in.visible).map((x) => x.render(h))
          ),
        ]),
      ]
    );
  }

  constructor(...opts: PartialQ<MoreButtonIn>[]) {
    super(moreButtonDefaults, ...opts);
  }
}

const more = (opts?: Partial<MoreButtonIn>) =>
  new MoreButton({ text: messages.more }, opts);

/**
 * left: Controls to show on left as desktop - typically "Cancel"
 * primary: Important controls to show on the right, and to always show on Mobile - typically "Save" / "OK" / "Confirm"
 * right: Controls to show on right as desktop in addition to primary. Often empty, but sometimes secondary buttons
 *
 * On mobile, if there is one control it will be shown, if more than one then everything other than primary is
 * wrapped under a "More" button.
 */
const dialogButtonLayoutDefaults = extendDefaults(controlDefaults, {
  left: [] as readonly IControl[],
  primary: [] as readonly IControl[],
  right: [] as readonly IControl[],
});

export type DialogButtonLayoutIn = typeof dialogButtonLayoutDefaults;

export class DialogButtonLayout extends TpControl<DialogButtonLayoutIn> {
  private _vStack = tpLayouts.vStack({ spaceBetween: 8 });
  private _desktopLeftRight = tpLayouts.leftRight();
  private _mobileMoreBtn = more({ fullWidth: true });

  render(h: any) {
    return this._vStack.render(h);
  }

  constructor(opts?: Partial<DialogButtonLayoutIn>) {
    super(dialogButtonLayoutDefaults, opts);

    const desktopLeftHStack = tpLayouts.hSpan({ gap: 8 });
    const desktopRightHStack = tpLayouts.hSpan({ gap: 8 });

    this._desktopLeftRight.setLeft(desktopLeftHStack);
    this._desktopLeftRight.setRight(desktopRightHStack);

    watchEffect(() => {
      const isMobile = screenSize.xs;
      const right = this.in.right.filter((x) => x.in.visible);
      const left = this.in.left.filter((x) => x.in.visible);
      const primary = this.in.primary.filter((x) => x.in.visible);

      const all = left.concat(right).concat(primary);

      if (isMobile) {
        desktopLeftHStack.setChildren([]);
        desktopRightHStack.setChildren([]);
        if (all.length < 2) {
          // Only 1 (or 0) buttons - just show it
          this._vStack.setChildren(all);
        } else {
          // 2 or more buttons - just show the primary (if any) and put the others in more.
          this._vStack.setChildren(
            [this._mobileMoreBtn as IControl].concat(primary)
          );
          this._mobileMoreBtn.in.buttons = left.concat(right);
        }
      } else {
        this._vStack.setChildren([this._desktopLeftRight]);
        desktopLeftHStack.setChildren(left);
        desktopRightHStack.setChildren(right.concat(primary));
      }

      for (const child of all) {
        const c = child as any;
        if (c && c.in && Object.hasOwn(c.in, "fullWidth")) {
          c.in.fullWidth = screenSize.xs;
        }
      }
    });
  }
}

const buttons = {
  ghost: (initialText: string, opts?: Partial<ButtonIn>) =>
    mergeIn(new Button("ghost", initialText), opts ?? {}),
  primary: (initialText: string, opts?: Partial<ButtonIn>) =>
    mergeIn(new Button("primary", initialText), opts ?? {}),
  primaryRoute: (
    initialText: string,
    initialTo: Location | null,
    opts?: Partial<ButtonIn>
  ) => {
    opts = Object.assign({}, opts ?? {});
    opts.toLocation = initialTo;
    return mergeIn(new Button("primary", initialText), opts);
  },
  secondary: (initialText: string, opts?: Partial<ButtonIn>) =>
    mergeIn(new Button("secondary", initialText), opts ?? {}),
  dialogButtonLayout: (opts?: Partial<DialogButtonLayoutIn>) =>
    new DialogButtonLayout(opts),
  more,
};

export { buttons };
